import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { formatDate } from '@utils/datetime';
import { framedByDefault } from '@utils/frames';
import { isPrefersReducedMotion } from '@utils/isPrefersReducedMotion';
import { getProductBenefits } from '@utils/product';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { type RefObject, useState } from 'react';

import { PRODUCT_TAG_NO_COA } from '../../../constants/product';
import { useProductCheckoutLink } from '../../../features/checkout/hooks/useProductCheckoutLink';
import { withFrameQuery } from '../../../features/checkout/utils/framingQuery';
import type { DrawByProductId } from '../../../features/draws/types';
import { useDrawStage } from '../../../hooks/useDrawStage';
import { useIsPreOrder } from '../../../hooks/useIsPreOrder';
import { useProductPricing } from '../../../hooks/useProductPricing';
import type { ProductGateway } from '../../../types/AvantArteProduct';
import type {
  NftCounterpartStoryblok,
  ProductDetailsStoryblok,
  ProductStoryblok,
} from '../../../types/generated-storyblok';
import type {
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../types/Purchasing';
import { ContentBlockVideo } from '../../ContentBlockVideo';
import { ProductRelatedDigitalProducts } from '../../ProductRelatedDigitalProducts/ProductRelatedDigitalProducts';
import ScrollAnchor from '../../ScrollAnchor';
import { getCustomDimensions } from '../Dimensions';
import { ProductAccordion } from './ProductAccordion';
import { ProductCounterpartsDrawer } from './ProductCounterpartsDrawer';
import { ProductCTA } from './ProductCTA';
import { ProductHero } from './ProductHero';
import { ProductReleaseInfo } from './ProductReleaseInfo';
import ProductVariantOptions from './ProductVariantOptions';

const ProductFeesDrawer = dynamic(() => import('./ProductFeesDrawer'), {
  ssr: false,
});

const ProductFramingDrawer = dynamic(() => import('./ProductFramingDrawer'), {
  ssr: false,
});

export type Props<TProduct extends ProductPurchaseDetails> = {
  analytics: Analytics;
  draw?: DrawByProductId;
  framingDrawerVisible: boolean;
  setFramingDrawerVisible: (visible: boolean) => void;
  product: ProductType;
  purchasing: ProductPurchasingController<TProduct>;
  productsGateway: ProductGateway;
  releaseCollectionGalleryRef: RefObject<HTMLDivElement>;
};

const ProductAbstractInner = <TProduct extends ProductPurchaseDetails>(
  props: Props<TProduct>,
  ctaWrapperRef: React.ForwardedRef<HTMLAnchorElement>,
) => {
  const { t } = useTranslation(['product']);
  const [emitter] = useAnalyticsEmitter();

  const benefits = getProductBenefits(
    props.product as unknown as ProductStoryblok,
    props.purchasing,
  );
  const productArtists = (
    props.product as unknown as ProductStoryblok
  ).productArtist.reduce<{ name: string; slug: string }[]>(
    (acc, { artist }) => {
      if (!artist || typeof artist === 'string') {
        return acc;
      }
      const { name, slug } = artist;
      const isEstate = artist.content?.isEstate;
      const estateName = artist.content?.estateName;
      acc.push({ name: isEstate && estateName ? estateName : name, slug });

      return acc;
    },
    [],
  );
  const productPrice = useProductPricing({
    purchasing: props.purchasing,
  });

  const productDetails = props.product?.details?.[0] as ProductDetailsStoryblok;

  const relatedDigitalProducts = props.product
    .relatedDigitalProducts as unknown as NftCounterpartStoryblok[];
  const highlightVideo = props.product.highlightVideo?.map((blok) => {
    return { ...blok, analytics: props.analytics };
  });

  const drawStage = useDrawStage(props.draw);
  const isPreOrder = useIsPreOrder(props.draw);

  const [
    relatedDigitalProductsDrawerVisible,
    setRelatedDigitalProductsDrawerVisible,
  ] = useState(false);
  const [feesDrawVisible, setFeesDrawVisible] = useState(false);

  const router = useRouter();
  const checkoutLink = useProductCheckoutLink();

  const onShowRelatedDigitalProducts = () =>
    setRelatedDigitalProductsDrawerVisible(true);
  const onShowTaxDetails = () => setFeesDrawVisible(true);
  const onShowFramingDetails = () => props.setFramingDrawerVisible(true);

  const childProps = { ...props, onShowTaxDetails, onShowFramingDetails };

  return (
    <aside className="mx-auto flex w-full max-w-md flex-col gap-y-9 pt-6">
      <ProductHero
        artists={productArtists}
        price={productPrice}
        title={props.product.title}
      />

      <ProductVariantOptions
        product={props.product as unknown as ProductStoryblok}
        productsGateway={props.productsGateway}
        purchasingController={props.purchasing}
      />

      <ProductReleaseInfo
        available={props.purchasing?.product?.available}
        closingDate={props.product.closingDate}
        dimensions={productDetails?.dimensions?.[0]}
        dispatchDate={props.purchasing.dispatchDate}
        isFramed={Boolean(props.purchasing.frame)}
        editionNote={props.product.editionNote}
        editionSize={props.product.editionSize}
        framingOffered={props.purchasing.product?.framingOffered}
        freeShipping={props.purchasing.product?.freeShipping}
        hideShippingEstimate={false}
        medium={props.product.medium}
        onEditionSizeAnchorClick={() => {
          emitter('', 'collection clicked');
          props.releaseCollectionGalleryRef.current?.scrollIntoView({
            behavior: isPrefersReducedMotion() ? 'instant' : 'smooth',
          });
        }}
        releaseCollectionGallery={props.product.releaseCollectionGallery}
        releaseNote={props.product.releaseNote}
        releaseType={props.product.releaseType}
        tags={props.purchasing.product?.tags}
      />

      {props.product.highlightVideo?.length > 0 && (
        <div className="md:hidden">
          <ContentBlockVideo blok={highlightVideo[0]} />
        </div>
      )}
      <div className="flex flex-col gap-3">
        {relatedDigitalProducts?.length > 0 && (
          <ProductRelatedDigitalProducts
            image={
              Array.isArray(relatedDigitalProducts[0]?.media)
                ? relatedDigitalProducts[0].media[0].component === 'image'
                  ? relatedDigitalProducts[0]?.media[0]?.image
                  : relatedDigitalProducts[0]?.media[0]?.coverImage
                : relatedDigitalProducts[0]?.media
            }
            label={t('digitalProducts.tile.description')}
            onClick={() => {
              onShowRelatedDigitalProducts();
              emitter('', 'paired NFT clicked');
            }}
          />
        )}

        <div>
          <ScrollAnchor
            ref={ctaWrapperRef}
            id="transactionBlock"
            offset={-72}
          />
          <ProductCTA {...childProps} />
        </div>
      </div>

      <ProductAccordion
        authentication={productDetails.authentication}
        benefits={benefits}
        customDimensions={getCustomDimensions(productDetails)}
        description={productDetails.description}
        drawStage={drawStage}
        eligibility={productDetails.eligibility}
        finishing={productDetails.finishing}
        framingDetails={productDetails.framingOptions}
        isFrameIncluded={framedByDefault(
          props.purchasing?.product?.framingOffered,
        )}
        isPreOrder={isPreOrder}
        isShippingRequired={props.purchasing.product?.shippingRequired}
        launchDate={formatDate(props.product.launchDate) || undefined}
        medium={productDetails.medium}
        noCoa={
          props.purchasing.product?.tags?.includes(PRODUCT_TAG_NO_COA) ?? false
        }
        noTaxCharged={props.purchasing.product?.noTaxCharged}
        onFramingDetailsClick={() => onShowFramingDetails()}
        onTaxDetailsClick={() => onShowTaxDetails()}
        productId={props.product.id[0].id}
        releaseType={props.product.releaseType}
        tags={props.purchasing.product?.tags}
      />

      {relatedDigitalProducts?.length > 0 && (
        <ProductCounterpartsDrawer
          counterparts={relatedDigitalProducts}
          isVisible={relatedDigitalProductsDrawerVisible}
          setIsVisible={setRelatedDigitalProductsDrawerVisible}
        />
      )}
      <ProductFeesDrawer
        {...childProps}
        onClose={() => setFeesDrawVisible(false)}
        shown={feesDrawVisible}
      />
      <ProductFramingDrawer
        {...childProps}
        onClose={() => props.setFramingDrawerVisible(false)}
        onSubmit={
          checkoutLink
            ? (frame) => {
                void router.push(withFrameQuery(checkoutLink, frame));
              }
            : undefined
        }
        purchasingController={props.purchasing}
        shown={props.framingDrawerVisible}
      />
    </aside>
  );
};

const ProductAbstract = React.forwardRef(ProductAbstractInner);

export default ProductAbstract;
